import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td } from '../components/ui/bits';
import { useRpc, useSel } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { agoLabel, fmtDateTime, fmtBytes } from '../lib/format';
import { FREE_LIMITS, VYO_LIMITS } from '../lib/business';

const PROTECTED_BACKUP = 'firm_programmes_backup_20260817_trackr';

export const OpsPage: React.FC = () => {
  const { data: cron, error: e1, isLoading } = useRpc<any[]>('admin_cron_health');
  const { data: audit } = useSel<any[]>(['audit-log'], () =>
    supabase.from('admin_audit_log').select('*').order('created_at', { ascending: false }).limit(50));
  const { data: backups } = useRpc<any[]>('admin_backup_tables');

  if (isLoading) return <LoadingRow />;

  return (
    <div>
      <PageHeader title="Ops" subtitle="Cron health, the audit trail of every admin action, data hygiene and the standing drift monitors." />

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section title="Cron jobs" sub="Everything pg_cron runs, with its last outcome and 7-day failure count.">
          {e1 && <ErrorPanel error={e1} />}
          {(cron ?? []).length === 0 ? <EmptyNote>No jobs</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Job</Th><Th>Schedule</Th><Th>Last run</Th><Th>Failures 7d</Th><Th>Runs 7d</Th></tr></thead>
              <tbody>
                {(cron ?? []).map(j => (
                  <tr key={j.jobname}>
                    <Td className="font-medium text-stone-900">{j.jobname}{!j.active && <Badge tone="stone" className="ml-1">Disabled</Badge>}</Td>
                    <Td className="text-stone-500">{j.schedule}</Td>
                    <Td>
                      <Badge tone={j.last_status === 'succeeded' ? 'emerald' : j.last_status ? 'rose' : 'stone'}>{j.last_status ?? 'never'}</Badge>
                      <span className="text-stone-400 ml-1.5">{agoLabel(j.last_start)}</span>
                    </Td>
                    <Td className={`tabular-nums ${Number(j.failed_7d) > 0 ? 'text-rose-600 font-bold' : ''}`}>{j.failed_7d}</Td>
                    <Td className="tabular-nums">{j.runs_7d}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        <Section title="Audit log" sub="Every admin mutation, from the RPCs and the admin API. Nothing writes tables from the browser.">
          {(audit ?? []).length === 0 ? <EmptyNote>No admin actions recorded yet</EmptyNote> : (
            <div className="space-y-1 max-h-[420px] overflow-y-auto">
              {(audit ?? []).map(a => (
                <details key={a.id} className="text-[12px]">
                  <summary className="cursor-pointer flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1 hover:bg-stone-50 rounded px-1">
                    <span className="font-medium text-stone-800 w-full sm:w-auto sm:flex-1 min-w-0">{a.action}</span>
                    <span className="text-stone-500">{a.target_kind}{a.target_id ? ` ${String(a.target_id).slice(0, 8)}` : ''}</span>
                    <span className="text-stone-400">{fmtDateTime(a.created_at)}</span>
                  </summary>
                  {a.payload && <pre className="text-[10.5px] bg-stone-50 rounded-md p-2 overflow-x-auto text-stone-600">{JSON.stringify(a.payload, null, 2)}</pre>}
                </details>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section
          title="Backup table sweeper"
          sub="Dated backups accumulating in public. Rule: 30 days then drop, except the Trackr provenance backup, which is under legal hold. Drops stay manual SQL for now; this is the inventory."
        >
          {(backups ?? []).length === 0 ? <EmptyNote>No backup tables</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Table</Th><Th>Rows</Th><Th>Row security</Th><Th>Note</Th></tr></thead>
              <tbody>
                {(backups ?? []).map(b => (
                  <tr key={b.name}>
                    <Td className="font-mono text-[11px] text-stone-700">{b.name}</Td>
                    <Td className="tabular-nums">{Number(b.approx_rows).toLocaleString()}</Td>
                    <Td>{b.rls_enabled ? 'on' : <Badge tone="rose">Off, publicly readable</Badge>}</Td>
                    <Td>{b.name === PROTECTED_BACKUP ? <Badge tone="amber">Legal hold, never drop</Badge> : ''}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        <Section title="Config checklist" sub="Facts verified during the 2026-08-19 rebuild. Re-verify when touching the related system.">
          <ul className="text-[12.5px] text-stone-600 space-y-1.5 list-disc pl-4">
            <li>REVALIDATE_SECRET: set on Vercel (route returns 401 without it). The cockpit’s revalidate button needs the same value as a Supabase secret named REVALIDATE_SECRET; until then it reports not configured.</li>
            <li>send-firm-alert: neutralised to a 410 tombstone on 2026-08-19; its trigger was already dropped.</li>
            <li>Leaked-password protection: still off in Supabase auth settings; turn it on in the dashboard.</li>
            <li>Marketing /api/indexnow fails open when its secret is unset, and its GET dumps the URL list; fix scheduled with the catalogue phase.</li>
            <li>Legacy /api/unsubscribe accepts any email with no rate limit; keep the token flow, retire or limit the email-entry flow.</li>
            <li>Resend is on a paid plan: no daily send cap applies.</li>
          </ul>
        </Section>
      </div>

      <Section
        title="Drift monitors"
        sub="Rules that live in several places at once. The database is the enforcement truth; these are the copies that have drifted before."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-[12px] font-bold text-stone-700 mb-1.5">The paid-access predicate, six copies</h3>
            <ul className="text-[12px] text-stone-600 space-y-1 list-disc pl-4">
              <li>Database: reserve_usage, check_usage, has_paid_access, is_effectively_paid (the truth).</li>
              <li>App client: services/stripe.ts isPaidSubscriptionRow.</li>
              <li>Email runtime: _shared/lifecycle.ts computeIsPaid.</li>
              <li>This cockpit renders the database classification only and holds no copy.</li>
            </ul>
            <p className="text-[11px] text-stone-400 mt-1.5">The single-source refactor is planned separately (PAID-ACCESS-SINGLE-SOURCE-2026-08-06).</p>
          </div>
          <div>
            <h3 className="text-[12px] font-bold text-stone-700 mb-1.5">Limit matrices, four copies</h3>
            <ul className="text-[12px] text-stone-600 space-y-1 list-disc pl-4">
              <li>Database reserve_usage and check_usage: enforcement truth.</li>
              <li>App services/limits.ts: the display copy.</li>
              <li>gemini-proxy PLAN_LIMITS: a deliberately looser backstop.</li>
              <li>coach-chat V2 base limits: known stale (cv 10, cover letter 10, research 5 against the app’s 30, 90, 25).</li>
            </ul>
            <p className="text-[11px] text-stone-400 mt-1.5">
              Also known: ChatBrief actually spends the research bucket, not cover letter, despite limits.ts’s comment. Free caps as of 29 Oct (services/limits.ts): HireVue {FREE_LIMITS.hv}, psychometric {FREE_LIMITS.psy}, live mock {FREE_LIMITS.mocks}, CV {FREE_LIMITS.cv}, Vyo {VYO_LIMITS.free_lifetime} lifetime.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
};
